import React from 'react';
import { Info, Activity, Calculator, Zap, Flame, Dumbbell, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о метаболизме и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-emerald-500" />
        Всё о метаболизме: что это такое и как его улучшить
      </h2>
      
      <div className="prose prose-emerald dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-emerald-500" />
          Что такое метаболизм?
        </h3>
        <p>
          <strong>Метаболизм</strong> — это совокупность всех химических процессов, происходящих в организме для поддержания жизни. Эти процессы позволяют нам расти, двигаться, думать, восстанавливаться и реагировать на окружающую среду. С точки зрения питания, метаболизм определяет, сколько калорий вам необходимо для поддержания жизненных функций.
        </p>

        <p>
          Метаболизм включает в себя два основных процесса:
        </p>
        <ul>
          <li><strong>Катаболизм</strong> — расщепление молекул с высвобождением энергии</li>
          <li><strong>Анаболизм</strong> — создание и построение новых клеток и тканей с использованием энергии</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-emerald-500" />
          Основные компоненты расхода энергии
        </h3>
        <ul>
          <li>
            <strong>Базальный метаболизм (BMR)</strong> — энергия, необходимая организму в состоянии покоя для поддержания жизненно важных функций (дыхание, кровообращение, работа мозга, поддержание температуры тела). BMR составляет 60-75% от общего расхода энергии.
          </li>
          <li>
            <strong>Термический эффект пищи (TEF)</strong> — энергия, расходуемая на переваривание, усвоение и хранение потребляемой пищи. Составляет около 10% от общего расхода.
          </li>
          <li>
            <strong>Расход энергии на физическую активность (EEPA)</strong> — энергия, потраченная на любую физическую активность, от простого движения до интенсивных тренировок. Может составлять от 15% до 30% и более от общего расхода.
          </li>
          <li>
            <strong>Неупражняемый термогенез (NEAT)</strong> — энергия, расходуемая на непроизвольные движения (дрожь, переминание с ноги на ногу, жестикуляция) и поддержание осанки.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-emerald-500" />
          Факторы, влияющие на метаболизм
        </h3>
        <p>Скорость метаболизма зависит от множества факторов, включая:</p>
        <ul>
          <li><strong>Генетика</strong> — наследственные факторы могут определять до 70% вашего базового метаболизма.</li>
          <li><strong>Возраст</strong> — с возрастом метаболизм естественным образом замедляется примерно на 2-3% каждое десятилетие после 20 лет.</li>
          <li><strong>Состав тела</strong> — мышечная ткань метаболически более активна, чем жировая. Каждый килограмм мышц сжигает примерно 13 ккал в день в состоянии покоя, тогда как килограмм жира — только 4-5 ккал.</li>
          <li><strong>Пол</strong> — у мужчин обычно более высокий метаболизм из-за большей мышечной массы и меньшего процента жира.</li>
          <li><strong>Гормоны</strong> — щитовидная железа, инсулин, кортизол и другие гормоны значительно влияют на скорость обмена веществ.</li>
          <li><strong>Физическая активность</strong> — регулярные тренировки временно увеличивают метаболизм и способствуют росту мышечной массы.</li>
          <li><strong>Питание</strong> — недостаточное питание может замедлить метаболизм как защитный механизм организма.</li>
          <li><strong>Сон и стресс</strong> — недостаток сна и хронический стресс негативно влияют на гормональный баланс и замедляют метаболизм.</li>
        </ul>

        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800 my-6">
          <h4 className="text-emerald-700 dark:text-emerald-300 font-medium">Знаете ли вы?</h4>
          <p className="text-emerald-600 dark:text-emerald-400 text-sm mb-0">
            Согласно исследованиям российских ученых, базальный метаболизм у жителей северных регионов России в среднем на 5-7% выше, чем у жителей южных областей, что связано с адаптацией организма к холодному климату и необходимостью поддерживать температуру тела.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-emerald-500" />
          Как увеличить скорость метаболизма?
        </h3>
        <ol>
          <li>
            <strong>Увеличивайте мышечную массу</strong> — силовые тренировки 2-3 раза в неделю помогут нарастить мышцы, которые сжигают больше калорий даже в состоянии покоя.
          </li>
          <li>
            <strong>Интервальные тренировки высокой интенсивности (HIIT)</strong> — короткие интенсивные тренировки продолжают "сжигать" калории даже после их окончания благодаря эффекту EPOC (избыточное потребление кислорода после тренировки).
          </li>
          <li>
            <strong>Достаточное потребление белка</strong> — организм тратит больше энергии на переваривание белка (20-30% калорий), чем на переваривание углеводов (5-10%) или жиров (0-3%).
          </li>
          <li>
            <strong>Регулярные приемы пищи</strong> — длительные перерывы между приемами пищи могут замедлить метаболизм. Оптимально есть каждые 3-4 часа.
          </li>
          <li>
            <strong>Достаточное потребление воды</strong> — исследования показывают, что выпивание 0,5 л воды может увеличить метаболизм на 10-30% на период до 1,5 часов.
          </li>
          <li>
            <strong>Здоровый сон</strong> — недостаток сна нарушает регуляцию гормонов, контролирующих аппетит и метаболизм.
          </li>
          <li>
            <strong>Управление стрессом</strong> — хронический стресс повышает уровень кортизола, что способствует накоплению жира в области живота и замедляет метаболизм.
          </li>
          <li>
            <strong>Термогенные продукты</strong> — некоторые продукты и специи (перец чили, имбирь, зеленый чай, кофе) могут временно ускорить метаболизм.
          </li>
        </ol>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Метаболические мифы, которым не стоит верить
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li><strong>Миф 1:</strong> "Eating late at night slows your metabolism" — время приема пищи не так важно, как общее количество потребляемых калорий.</li>
            <li><strong>Миф 2:</strong> "Certain foods, like grapefruit or celery, have negative calories" — хотя некоторые продукты требуют энергии для переваривания, нет продуктов с отрицательными калориями.</li>
            <li><strong>Миф 3:</strong> "Crash diets are effective for long-term weight loss" — резкое снижение калорий заставляет организм замедлять метаболизм для экономии энергии.</li>
            <li><strong>Миф 4:</strong> "Supplements can significantly boost your metabolism" — большинство добавок для ускорения метаболизма имеют минимальный эффект и могут быть небезопасны.</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Dumbbell className="w-5 h-5 text-emerald-500" />
          Особенности метаболизма по возрастным группам
        </h3>
        <h4>Дети и подростки (до 18 лет)</h4>
        <p>
          У детей и подростков метаболизм работает наиболее интенсивно из-за активного роста и развития. Важно обеспечить растущий организм всеми необходимыми питательными веществами, особенно качественными белками и микроэлементами. Ограничение калорий в этот период может негативно сказаться на развитии.
        </p>

        <h4>Молодые взрослые (18-30 лет)</h4>
        <p>
          В этом возрасте метаболизм достаточно высок, но начинает постепенно снижаться. Важно формировать здоровые привычки питания и физической активности, которые помогут поддерживать метаболизм в будущем. Обычно это период наименьших проблем с весом при отсутствии других факторов.
        </p>

        <h4>Взрослые (30-50 лет)</h4>
        <p>
          Метаболизм замедляется примерно на 2-3% каждое десятилетие. Мышечная масса начинает естественным образом снижаться, а способность организма перерабатывать углеводы ухудшается. В этот период особенно важно поддерживать мышечную массу через силовые тренировки и следить за качеством питания.
        </p>

        <h4>Пожилые люди (старше 50 лет)</h4>
        <p>
          Процесс потери мышечной массы (саркопения) ускоряется, что приводит к дальнейшему замедлению метаболизма. Гормональные изменения (менопауза у женщин, снижение тестостерона у мужчин) также влияют на метаболизм и распределение жира. Регулярные физические упражнения, особенно силовые, и достаточное потребление белка становятся критически важными для поддержания мышечной массы и здорового метаболизма.
        </p>

        <h3>Заключение</h3>
        <p>
          Понимание особенностей вашего метаболизма поможет более эффективно управлять весом и поддерживать здоровье. Помните, что здоровый метаболизм — это результат комплексного подхода, включающего физическую активность, правильное питание, достаточный сон и управление стрессом.
        </p>

        <p className="font-semibold mt-6">
          Наш калькулятор метаболизма поможет вам определить ваши энергетические потребности и составить план питания, соответствующий вашим целям!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;